/**
 * Paths Statistics Tab
 * Uses shared CampaignStatsDashboard with paths-specific data from Voluum.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useAuth, useFlowOptimizerTags, usePathPerformanceTags, usePathsWithStats } from '@/hooks';
import { statsTrackerId } from '@/features/trackers';
import { smswiserCampaignWorkspaceKeys } from '@/hooks/smswiser-campaign-workspace/keys';

import {
  CampaignStatsDashboard,
  TruncatedFlowItemCell,
  FILTER_FIELDS_PATHS,
  GROUP_BY_OPTIONS_PATHS,
  toRangeISO,
  toGenericStatsRowFromPath,
  useStatsTabState,
  useStatsCompareState,
  useFilteredAndSortedRowsGeneric,
  type GroupByKey,
  type GenericStatsRow,
} from '@/features/smswiser-campaigns/components/SmswiserCampaignStatsTab';

interface PathsStatsTabProps {
  countryKey: string;
  trafficCampaignId?: string | null;
  trafficProvider?: string | null;
  wiserIds: number[];
  workDate: string;
}

export const PathsStatsTab: FC<PathsStatsTabProps> = ({
  countryKey,
  trafficCampaignId,
  trafficProvider,
  wiserIds,
  workDate,
}) => {
  const { user } = useAuth();
  const state = useStatsTabState(workDate, 'paths');
  const range = useMemo(
    () => toRangeISO(state.fromDate, state.toDate),
    [state.fromDate, state.toDate],
  );

  const queryClient = useQueryClient();
  const prevRangeRef = useRef<{ from: string; to: string } | null>(null);
  useEffect(() => {
    const isInitial = prevRangeRef.current === null;
    prevRangeRef.current = range;
    if (isInitial) return;
    queryClient.removeQueries({
      queryKey: [...smswiserCampaignWorkspaceKeys.all, 'pathsWithStats'],
    });
  }, [range.from, range.to, queryClient]);

  const perfTagsQuery = usePathPerformanceTags(
    countryKey,
    wiserIds.length > 0 ? wiserIds : undefined,
    { from: range.from, to: range.to, tz: 'Europe/Paris', currency: state.currency },
  );
  const tagsByPathId = perfTagsQuery.data?.tagsByPathId ?? {};
  const tagDetailsByPathId = perfTagsQuery.data?.tagDetailsByPathId ?? {};
  const tagColors = perfTagsQuery.data?.tagColors;

  const flowTagsQuery = useFlowOptimizerTags(
    countryKey,
    trafficCampaignId ?? undefined,
    statsTrackerId(trafficProvider),
    'paths',
    Boolean(trafficCampaignId),
  );
  const flowTagsByPathId = flowTagsQuery.data?.tagsByItemId ?? {};
  const flowTagColors = flowTagsQuery.data?.tagColors;

  const query = usePathsWithStats({
    countryKey,
    trafficCampaignId: trafficCampaignId ?? undefined,
    wiserIds,
    from: range.from,
    to: range.to,
    currency: state.currency,
    tz: 'Europe/Paris',
    limit: state.length,
  });

  const compare = useStatsCompareState(state.fromDate, state.toDate, state.setDateRange);
  const compareRange = useMemo(
    () => toRangeISO(compare.compareFrom, compare.compareTo),
    [compare.compareFrom, compare.compareTo],
  );
  const compareQuery = usePathsWithStats({
    countryKey,
    trafficCampaignId: trafficCampaignId ?? undefined,
    wiserIds,
    from: compareRange.from,
    to: compareRange.to,
    currency: state.currency,
    tz: 'Europe/Paris',
    limit: state.length,
    enabled: compare.compareMode,
  });
  const compareById = useMemo(() => {
    const map = new Map<string, Record<string, number> | null>();
    (compareQuery.data?.rows ?? []).forEach((r, i) => {
      const g = toGenericStatsRowFromPath(r, i);
      map.set(g.id, g.voluum);
    });
    return map;
  }, [compareQuery.data?.rows]);

  const allPathRows = query.data?.rows ?? [];
  const allGenericRows = useMemo(
    () =>
      allPathRows.map((r, i) => {
        const base = toGenericStatsRowFromPath(r, i);
        const pathId = r.pathId ? String(r.pathId).trim() : '';
        const tags =
          pathId
            ? (tagsByPathId[pathId] ??
              Object.entries(tagsByPathId).find(
                ([k]) => k.toLowerCase() === pathId.toLowerCase(),
              )?.[1]) ?? []
            : [];
        return { ...base, performanceTags: tags };
      }),
    [allPathRows, tagsByPathId],
  );

  const { rows, groups } = useFilteredAndSortedRowsGeneric(
    allGenericRows,
    state.filtersState,
    state.sort,
    workDate,
    state.groupBy,
  );

  const totals = query.data?.totals ?? {};

  const availableTags = useMemo(() => {
    const set = new Set<string>();
    for (const name of Object.keys(tagColors ?? {})) if (name.trim()) set.add(name.trim());
    for (const tags of Object.values(tagsByPathId)) {
      for (const t of tags) if (t && String(t).trim()) set.add(String(t).trim());
    }
    return [...set].sort();
  }, [tagColors, tagsByPathId]);

  const tagDetailsLookup = useCallback(
    (pathId: string) => {
      if (!pathId) return undefined;
      const direct = tagDetailsByPathId[pathId];
      if (direct) return direct;
      const entry = Object.entries(tagDetailsByPathId).find(
        ([k]) => k.toLowerCase() === pathId.toLowerCase(),
      );
      return entry?.[1];
    },
    [tagDetailsByPathId],
  );

  const renderFirstCell = useCallback(
    (row: GenericStatsRow) => {
      const pathId = row.id;
      const performanceTags = pathId
        ? (tagsByPathId[pathId] ??
          Object.entries(tagsByPathId).find(
            ([k]) => k.toLowerCase() === pathId.toLowerCase(),
          )?.[1])
        : undefined;
      const performanceTagDetails = pathId ? tagDetailsLookup(pathId) : undefined;
      return (
        <TruncatedFlowItemCell
          itemId={pathId}
          label={row.label ?? '—'}
          itemType="path"
          performanceTags={performanceTags}
          performanceTagDetails={performanceTagDetails}
          tagColors={tagColors}
          flowTags={pathId ? flowTagsByPathId[pathId] : undefined}
          flowTagColors={flowTagColors}
        />
      );
    },
    [tagsByPathId, tagDetailsLookup, tagColors, flowTagsByPathId, flowTagColors],
  );

  return (
    <CampaignStatsDashboard
      tab="paths"
      title="Paths"
      emptyMessage="No paths found for this range."
      loadingTitle="Loading path performance data"
      loadingDesc="Fetching tracker metrics grouped by path."
      errorPrefix="Failed to load path stats."
      firstColumnLabel="Path"
      firstColumnSortKey="path"
      rowUnitLabel="path"
      filterFields={FILTER_FIELDS_PATHS}
      groupByOptions={GROUP_BY_OPTIONS_PATHS}
      rows={rows}
      totals={totals}
      groups={groups}
      totalRowCount={query.data?.total ?? allGenericRows.length}
      allRowsLength={allGenericRows.length}
      currency={query.data?.currency ?? 'USD'}
      fromDate={state.fromDate}
      toDate={state.toDate}
      onFromChange={state.setFromDate}
      onToChange={state.setToDate}
      onDateRangeChange={state.setDateRange}
      length={state.length}
      onLengthChange={state.setLength}
      filtersState={state.filtersState}
      setFilters={state.setFilters}
      defaultFilter={state.defaultFilter}
      groupBy={state.groupBy}
      setGroupBy={(v) => state.setGroupBy(v as GroupByKey)}
      collapsedGroups={state.collapsedGroups}
      setCollapsedGroups={state.setCollapsedGroups}
      toggleGroup={state.toggleGroup}
      sort={state.sort}
      onSortClick={state.handleSortClick}
      selectedRows={state.selectedRows}
      setSelectedRows={state.setSelectedRows}
      renderFirstCell={renderFirstCell}
      isError={query.isError ?? false}
      error={query.error}
      isLoading={query.isLoading ?? false}
      isFetching={query.isFetching ?? false}
      dataUpdatedAt={query.dataUpdatedAt}
      refetch={() => query.refetch()}
      showLimitSelect
      showGroupBySelect={false}
      firstColumnClassName="min-w-[280px] md:min-w-[480px]"
      csvExportUserId={user?.id}
      compareCapable
      compareMode={compare.compareMode}
      onToggleCompare={compare.toggleCompareMode}
      compareFromDate={compare.compareFrom}
      compareToDate={compare.compareTo}
      onCompareFromChange={compare.setCompareFrom}
      onCompareToChange={compare.setCompareTo}
      onCompareRangeChange={compare.setCompareRange}
      compareById={compareById}
      compareTotals={compareQuery.data?.totals ?? {}}
      onComparePreset={compare.applyPreset}
      granularity={compare.granularity}
      onGranularityChange={compare.setGranularity}
      countryKey={countryKey}
      wiserIds={wiserIds}
      tagFilterOptions={availableTags}
      tagFilterColors={tagColors}
      tagsLoading={perfTagsQuery.isLoading}
    />
  );
};

export default PathsStatsTab;
